import { useMemo } from 'react';
import { create } from 'zustand';
import type { TodoCounts, TodoItem, TodoTask, TodosResponse } from '@goodchat/shared/types';
import type { TodoStatusFilter, TodoType } from '@goodchat/shared/constants';

/**
 * Todos store — one copy of every todo, many views over it.
 *
 * `itemsById` is the ONLY place item content lives. A view (the focused
 * cross-org page, or the contextual pane inside one Chat/Room) holds nothing
 * but an ordered list of ids plus its own paging state. Completing a task in
 * the pane therefore updates the focused page for free, and the two can never
 * disagree — which is the whole reason this is not two stores.
 *
 * Ordering is the server's: OLDEST-FIRST, on purpose. What you have ignored
 * longest sits at the top. Never re-sort a page newest-first for display.
 *
 * ── The rule this file exists to enforce ─────────────────────────────────
 * NEVER return a fresh array or object literal from a Zustand selector
 * (`s.views[key] ?? []`). A literal is a new reference on every store change,
 * so the component re-renders, which re-runs the selector, which allocates
 * again — React error #185, an infinite loop. This project has shipped that
 * bug twice. Every fallback below is a module-level frozen constant, and the
 * id → item projection is memoized inside `useTodosView`, never in a selector.
 */

// ============================================================
// Scope — what a view is looking at
// ============================================================

export interface TodosScope {
  /** Omit for the cross-org focused view. */
  orgId?: string;
  /** Set for the contextual pane inside one Chat/Room. */
  areaId?: string;
  /** Defaults to 'open', matching the server. */
  status?: TodoStatusFilter;
  /** Omit for every type. */
  types?: readonly TodoType[];
}

interface NormalizedScope {
  orgId?: string;
  areaId?: string;
  status: TodoStatusFilter;
  types: readonly TodoType[] | null;
}

function normalizeScope(scope: TodosScope): NormalizedScope {
  const types = scope.types && scope.types.length > 0 ? [...scope.types].sort() : null;
  return {
    orgId: scope.orgId,
    areaId: scope.areaId,
    status: scope.status ?? 'open',
    types,
  };
}

/**
 * Deterministic cache key for a scope. Exported so a caller can address a view
 * it did not create, but every action here takes the scope itself — no
 * component should be juggling keys.
 */
export function todosViewKey(scope: TodosScope = {}): string {
  const n = normalizeScope(scope);
  return `org:${n.orgId ?? '*'}|area:${n.areaId ?? '*'}|status:${n.status}|type:${n.types?.join('+') ?? 'all'}`;
}

/** Query params for `fetchTodos` — structurally a `FetchTodosParams`. */
export function todosFetchParams(scope: TodosScope): {
  orgId?: string;
  areaId?: string;
  type?: readonly TodoType[];
  status: TodoStatusFilter;
} {
  const n = normalizeScope(scope);
  return {
    ...(n.orgId ? { orgId: n.orgId } : {}),
    ...(n.areaId ? { areaId: n.areaId } : {}),
    ...(n.types ? { type: n.types } : {}),
    status: n.status,
  };
}

/**
 * Does this item fall inside the scope this view's COUNTS cover?
 *
 * Verified against the running API: `counts` responds to orgId, areaId and
 * status but NOT to the `type` filter — asking for `?type=task` still returns
 * counts for all four sources. So counting and listing are two different
 * questions, and conflating them makes a type-filtered tab's badge drift.
 *
 * Note the status rule: a SNOOZED reply-later is not "open" — the server hides
 * it until it comes due, and so must we, or the badge says 3 over an empty list
 * (the inbox bug this surface was built to avoid).
 */
function scopeAdmits(scope: NormalizedScope, item: TodoItem): boolean {
  if (scope.orgId && item.orgId !== scope.orgId) return false;
  if (scope.areaId && item.areaId !== scope.areaId) return false;
  if (scope.status === 'open') return item.status === 'open';
  if (scope.status === 'done') return item.status === 'done';
  return true;
}

/**
 * Should this item appear in this view's LIST? Scope plus the type filter, so a
 * locally-inserted item lands in exactly the views a refetch would have put it.
 */
function viewAdmits(scope: NormalizedScope, item: TodoItem): boolean {
  if (!scopeAdmits(scope, item)) return false;
  return !scope.types || scope.types.includes(item.type);
}

// ============================================================
// Stable empties — the anti-#185 constants
// ============================================================

export const EMPTY_TODO_COUNTS: TodoCounts = Object.freeze({
  acks: 0,
  replyLater: 0,
  polls: 0,
  tasks: 0,
  total: 0,
});

export const EMPTY_TODO_ITEMS: readonly TodoItem[] = Object.freeze([]);

const EMPTY_IDS: string[] = Object.freeze([]) as unknown as string[];

export interface TodosView {
  /** TodoItem ids in server order (oldest-first). */
  ids: string[];
  hasMore: boolean;
  /** Opaque keyset cursor for the next page. */
  nextCursor: string | null;
  /** Counts for THIS scope, from the same response as the items. */
  counts: TodoCounts;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  /** ms epoch of the last successful load; null = never loaded. */
  loadedAt: number | null;
  scope: NormalizedScope;
}

const DEFAULT_SCOPE: NormalizedScope = Object.freeze({
  orgId: undefined,
  areaId: undefined,
  status: 'open' as TodoStatusFilter,
  types: null,
});

/** The one object every un-loaded view resolves to. Frozen, never copied. */
export const EMPTY_TODOS_VIEW: TodosView = Object.freeze({
  ids: EMPTY_IDS,
  hasMore: false,
  nextCursor: null,
  counts: EMPTY_TODO_COUNTS,
  loading: false,
  loadingMore: false,
  error: null,
  loadedAt: null,
  scope: DEFAULT_SCOPE,
});

// ============================================================
// Count bookkeeping
// ============================================================

const COUNT_FIELD: Record<TodoType, 'acks' | 'replyLater' | 'polls' | 'tasks'> = {
  ack: 'acks',
  reply_later: 'replyLater',
  poll: 'polls',
  task: 'tasks',
};

/** Clamped at zero, and returns the SAME object when nothing moved. */
function adjustCounts(counts: TodoCounts, type: TodoType, delta: number): TodoCounts {
  const field = COUNT_FIELD[type];
  const next = Math.max(0, counts[field] + delta);
  const applied = next - counts[field];
  if (applied === 0) return counts;
  return { ...counts, [field]: next, total: Math.max(0, counts.total + applied) };
}

/**
 * Chronological, with an id tiebreak.
 *
 * `sortAt` off the feed is microsecond-precision (`to_char(... .US)`) while a
 * task payload's timestamps are millisecond JS ISO, so these are compared as
 * instants rather than strings — a raw string compare would misplace a locally
 * created task against server rows from the same millisecond.
 */
function compareSortAt(a: TodoItem, b: TodoItem): number {
  const at = Date.parse(a.sortAt);
  const bt = Date.parse(b.sortAt);
  if (!Number.isNaN(at) && !Number.isNaN(bt) && at !== bt) return at - bt;
  if (a.sortAt !== b.sortAt) return a.sortAt < b.sortAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * A `TodoTask` as the feed would have rendered it. Mirrors source 4 of
 * `GET /todos` field for field, so an optimistic insert is indistinguishable
 * from a refetch. `orgName`/`orgColor` are server enrichment — carried over
 * from the previous item when there is one, otherwise left for the caller.
 */
export function todoItemFromTask(
  task: TodoTask,
  previous?: TodoItem,
  org?: { name?: string; color?: string | null },
): TodoItem {
  return {
    id: `task:${task.id}`,
    type: 'task',
    status: task.completedAt ? 'done' : 'open',
    // Server: coalesce(due_at, created_at).
    sortAt: task.dueAt ?? task.createdAt,
    dueAt: task.dueAt,
    orgId: task.orgId,
    orgName: org?.name ?? previous?.orgName ?? '',
    orgColor: org?.color ?? previous?.orgColor ?? null,
    areaId: task.areaId,
    areaName: task.areaName,
    threadId: null,
    threadTitle: null,
    messageId: task.messageId,
    messageType: null,
    authorDisplayName: task.createdBy.displayName,
    authorProfilePictureUrl: task.createdBy.profilePictureUrl,
    contentPreview: task.title,
    createdAt: task.createdAt,
    task,
  };
}

// ============================================================
// Store
// ============================================================

interface TodosState {
  /** Single source of truth for item content. */
  itemsById: Record<string, TodoItem>;
  /** Ordered id lists + paging state, keyed by `todosViewKey`. */
  views: Record<string, TodosView>;
  /**
   * Cross-org OPEN counts for the nav badge — exactly what `todos.update`
   * carries. Distinct from a view's counts, which are scoped.
   */
  badgeCounts: TodoCounts;
  /**
   * Bumped whenever realtime says the server-side truth moved. A mounted view
   * can `useEffect` on it to refetch without the store nuking lists mid-scroll.
   */
  revision: number;

  beginLoad: (scope: TodosScope, mode: 'replace' | 'more') => void;
  setPage: (scope: TodosScope, res: TodosResponse) => void;
  appendPage: (scope: TodosScope, res: TodosResponse) => void;
  setError: (scope: TodosScope, error: string | null) => void;

  /** Put an item in every view whose scope admits it (and nowhere else). */
  insertItem: (item: TodoItem) => void;
  /** Merge fields, then re-file the item across views if admission changed. */
  updateItem: (id: string, fields: Partial<TodoItem>) => void;
  /** Drop everywhere, decrementing the counts of the views that held it. */
  removeItem: (id: string, opts?: { adjustBadge?: boolean }) => void;

  /** Apply a server task payload: merge, then re-file. */
  applyTask: (task: TodoTask, org?: { name?: string; color?: string | null }) => void;
  removeTask: (taskId: string) => void;

  setBadgeCounts: (counts: TodoCounts) => void;
  /** `todos.update` arrived: adopt its counts and tell views to refetch. */
  applyRealtimeCounts: (counts: TodoCounts) => void;
  /** Forget every cached list (keeps the badge). Use on logout/org switch. */
  invalidate: () => void;
  reset: () => void;
}

/** Insert an id into an ordered list at its chronological position. */
function insertId(ids: string[], item: TodoItem, itemsById: Record<string, TodoItem>): string[] {
  if (ids.includes(item.id)) return ids;
  let i = 0;
  while (i < ids.length) {
    const other = itemsById[ids[i]];
    if (other && compareSortAt(other, item) > 0) break;
    i++;
  }
  const next = ids.slice();
  next.splice(i, 0, item.id);
  return next;
}

/**
 * Re-file one item across every view, moving its list membership and its count
 * INDEPENDENTLY — a `?type=task` view still counts a poll it will never list.
 *
 * `previous` is the item as the store last knew it; absent means brand new.
 * Views that are unaffected keep their exact object reference, so unrelated
 * components do not re-render.
 */
function refileItem(
  views: Record<string, TodosView>,
  item: TodoItem,
  itemsById: Record<string, TodoItem>,
  previous?: TodoItem,
): Record<string, TodosView> {
  let changed = false;
  const next: Record<string, TodosView> = {};
  for (const [key, view] of Object.entries(views)) {
    const inList = view.ids.includes(item.id);
    const shouldList = viewAdmits(view.scope, item);
    const wasCounted = previous ? scopeAdmits(view.scope, previous) : false;
    const isCounted = scopeAdmits(view.scope, item);

    if (inList === shouldList && wasCounted === isCounted) {
      next[key] = view;
      continue;
    }
    changed = true;

    let ids = view.ids;
    if (shouldList && !inList) ids = insertId(view.ids, item, itemsById);
    else if (!shouldList && inList) ids = view.ids.filter((id) => id !== item.id);

    let counts = view.counts;
    if (isCounted && !wasCounted) counts = adjustCounts(counts, item.type, 1);
    else if (!isCounted && wasCounted) counts = adjustCounts(counts, item.type, -1);

    next[key] = { ...view, ids, counts };
  }
  return changed ? next : views;
}

/**
 * The item is gone. Drop it from every list that showed it and from every
 * count that included it — again separately, because a `?type=task` view
 * counted the poll it never listed.
 */
function dropFromViews(
  views: Record<string, TodosView>,
  item: TodoItem,
): Record<string, TodosView> {
  let changed = false;
  const next: Record<string, TodosView> = {};
  for (const [key, view] of Object.entries(views)) {
    const inList = view.ids.includes(item.id);
    const wasCounted = scopeAdmits(view.scope, item);
    if (!inList && !wasCounted) {
      next[key] = view;
      continue;
    }
    changed = true;
    next[key] = {
      ...view,
      ids: inList ? view.ids.filter((existingId) => existingId !== item.id) : view.ids,
      counts: wasCounted ? adjustCounts(view.counts, item.type, -1) : view.counts,
    };
  }
  return changed ? next : views;
}

export const useTodosStore = create<TodosState>((set) => ({
  itemsById: {},
  views: {},
  badgeCounts: EMPTY_TODO_COUNTS,
  revision: 0,

  beginLoad: (scope, mode) =>
    set((s) => {
      const key = todosViewKey(scope);
      const view = s.views[key] ?? { ...EMPTY_TODOS_VIEW, scope: normalizeScope(scope) };
      return {
        views: {
          ...s.views,
          [key]: {
            ...view,
            loading: mode === 'replace' ? true : view.loading,
            loadingMore: mode === 'more',
            error: null,
          },
        },
      };
    }),

  setPage: (scope, res) =>
    set((s) => {
      const key = todosViewKey(scope);
      const itemsById = { ...s.itemsById };
      for (const item of res.items) itemsById[item.id] = item;
      return {
        itemsById,
        views: {
          ...s.views,
          [key]: {
            ids: res.items.map((i) => i.id),
            hasMore: res.hasMore,
            nextCursor: res.nextCursor,
            counts: res.counts,
            loading: false,
            loadingMore: false,
            error: null,
            loadedAt: Date.now(),
            scope: normalizeScope(scope),
          },
        },
      };
    }),

  appendPage: (scope, res) =>
    set((s) => {
      const key = todosViewKey(scope);
      const view = s.views[key];
      // No page to append to — treat it as a fresh load rather than dropping it.
      if (!view) {
        const itemsById = { ...s.itemsById };
        for (const item of res.items) itemsById[item.id] = item;
        return {
          itemsById,
          views: {
            ...s.views,
            [key]: {
              ids: res.items.map((i) => i.id),
              hasMore: res.hasMore,
              nextCursor: res.nextCursor,
              counts: res.counts,
              loading: false,
              loadingMore: false,
              error: null,
              loadedAt: Date.now(),
              scope: normalizeScope(scope),
            },
          },
        };
      }
      const itemsById = { ...s.itemsById };
      const seen = new Set(view.ids);
      const ids = view.ids.slice();
      for (const item of res.items) {
        itemsById[item.id] = item;
        // The keyset cursor should make repeats impossible, but a duplicate id
        // in a React list is a silent rendering bug — cheap to just not.
        if (!seen.has(item.id)) {
          seen.add(item.id);
          ids.push(item.id);
        }
      }
      return {
        itemsById,
        views: {
          ...s.views,
          [key]: {
            ...view,
            ids,
            hasMore: res.hasMore,
            nextCursor: res.nextCursor,
            counts: res.counts,
            loading: false,
            loadingMore: false,
            error: null,
            loadedAt: Date.now(),
          },
        },
      };
    }),

  setError: (scope, error) =>
    set((s) => {
      const key = todosViewKey(scope);
      const view = s.views[key] ?? { ...EMPTY_TODOS_VIEW, scope: normalizeScope(scope) };
      return {
        views: { ...s.views, [key]: { ...view, loading: false, loadingMore: false, error } },
      };
    }),

  insertItem: (item) =>
    set((s) => {
      const previous = s.itemsById[item.id];
      const itemsById = { ...s.itemsById, [item.id]: item };
      return { itemsById, views: refileItem(s.views, item, itemsById, previous) };
    }),

  updateItem: (id, fields) =>
    set((s) => {
      const existing = s.itemsById[id];
      if (!existing) return s;
      const merged: TodoItem = { ...existing, ...fields };
      const itemsById = { ...s.itemsById, [id]: merged };
      return { itemsById, views: refileItem(s.views, merged, itemsById, existing) };
    }),

  removeItem: (id, opts) =>
    set((s) => {
      const item = s.itemsById[id];
      if (!item) return s;
      const itemsById = { ...s.itemsById };
      delete itemsById[id];

      // Acknowledging, voting and resolving reply-later do NOT emit
      // `todos.update` — only task writes do. Without this the nav badge stays
      // stale until the next full fetch, which is precisely the "badge says 3
      // over an empty list" failure this surface exists to avoid.
      const adjustBadge = opts?.adjustBadge ?? item.status === 'open';
      return {
        itemsById,
        views: dropFromViews(s.views, item),
        badgeCounts: adjustBadge ? adjustCounts(s.badgeCounts, item.type, -1) : s.badgeCounts,
      };
    }),

  applyTask: (task, org) =>
    set((s) => {
      const id = `task:${task.id}`;
      const previous = s.itemsById[id];
      const item = todoItemFromTask(task, previous, org);
      const itemsById = { ...s.itemsById, [id]: item };
      return { itemsById, views: refileItem(s.views, item, itemsById, previous) };
    }),

  removeTask: (taskId) =>
    set((s) => {
      const id = `task:${taskId}`;
      const item = s.itemsById[id];
      if (!item) return s;
      const itemsById = { ...s.itemsById };
      delete itemsById[id];
      // Task writes DO broadcast `todos.update`, so the badge corrects itself
      // over the wire — no optimistic decrement here.
      return { itemsById, views: dropFromViews(s.views, item) };
    }),

  setBadgeCounts: (counts) => set({ badgeCounts: counts }),

  applyRealtimeCounts: (counts) =>
    set((s) => ({ badgeCounts: counts, revision: s.revision + 1 })),

  invalidate: () => set({ itemsById: {}, views: {} }),

  reset: () =>
    set({ itemsById: {}, views: {}, badgeCounts: EMPTY_TODO_COUNTS, revision: 0 }),
}));

// ============================================================
// Hooks — the ONLY sanctioned way to read a list out of this store
// ============================================================

export interface TodosViewSnapshot {
  key: string;
  items: readonly TodoItem[];
  hasMore: boolean;
  nextCursor: string | null;
  counts: TodoCounts;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  loadedAt: number | null;
}

/**
 * Read one view. The id → item projection happens in `useMemo`, NOT in the
 * selector: a selector that mapped ids to items would allocate a new array on
 * every store change and loop forever (React #185).
 *
 * `scope` may be a fresh object literal every render — only its derived key
 * matters, and that is a primitive.
 */
export function useTodosView(scope: TodosScope): TodosViewSnapshot {
  const key = todosViewKey(scope);
  const view = useTodosStore((s) => s.views[key] ?? EMPTY_TODOS_VIEW);
  const itemsById = useTodosStore((s) => s.itemsById);

  const items = useMemo(() => {
    if (view.ids.length === 0) return EMPTY_TODO_ITEMS;
    const out: TodoItem[] = [];
    for (const id of view.ids) {
      const item = itemsById[id];
      if (item) out.push(item);
    }
    return out;
  }, [view.ids, itemsById]);

  return useMemo(
    () => ({
      key,
      items,
      hasMore: view.hasMore,
      nextCursor: view.nextCursor,
      counts: view.counts,
      loading: view.loading,
      loadingMore: view.loadingMore,
      error: view.error,
      loadedAt: view.loadedAt,
    }),
    [key, items, view],
  );
}

/** Nav badge. A primitive, so it is safe anywhere. */
export function useTodoBadgeTotal(): number {
  return useTodosStore((s) => s.badgeCounts.total);
}

/** Stable store reference — never a literal. */
export function useTodoBadgeCounts(): TodoCounts {
  return useTodosStore((s) => s.badgeCounts);
}

/** Increments when realtime says the server truth moved; refetch on change. */
export function useTodosRevision(): number {
  return useTodosStore((s) => s.revision);
}

// ============================================================
// Realtime
// ============================================================

/**
 * Handle the `todos.update` WS event. Adopts the server's cross-org counts and
 * bumps `revision` so mounted views refetch — the counts alone cannot tell us
 * WHICH row appeared or vanished.
 *
 * Only task writes broadcast this today; acknowledgments, poll votes and
 * reply-later resolutions adjust locally via `removeItem`.
 */
export function applyTodosUpdateEvent(event: { counts: TodoCounts }): void {
  useTodosStore.getState().applyRealtimeCounts(event.counts);
}
